import {
  Directive,
  ElementRef,
  Injector,
  OnInit,
  Renderer2,
  ViewContainerRef,
  ComponentRef,
  DestroyRef,
  Signal,
  effect,
  inject,
  input,
  model,
  untracked,
} from '@angular/core';
import { DynamicVariablesService } from '@core/services/dynamic-variables.service';
import { VariablesManager } from '../components/dynamic-variables/variables-manager/variables-manager';

/**
 * Вешается на textarea/input. Регистрирует source (`appVariableTracker`)
 * в scope (`variableScope`) внутри DynamicVariablesService. Все source'ы
 * одного scope делят одну OpenAPI-схему: var добавляется когда появляется
 * в тексте одного из source'ов, удаляется когда исчезает из ВСЕХ; метаданные
 * (title/type/fallback) правятся в drawer'е `<VariablesManager>` и сохраняются
 * пока var используется хотя бы одним текстом scope.
 *
 * Пример: `<textarea [variableScope]="'agent'" [appVariableTracker]="'system-prompt'"
 *          [text]="agent().system_prompt" [(variablesSchema)]="agent().variables_open_api_schema">`
 *
 * Init через ngOnInit (не constructor) — required inputs недоступны раньше;
 * effect'ы поэтому создаются с явным injector'ом.
 */
@Directive({
  selector: '[appVariableTracker]',
  standalone: true,
})
export class VariableTrackerDirective implements OnInit {
  readonly appVariableTracker = input.required<string>();
  readonly variableScope = input.required<string>();
  readonly text = input<string>('');
  readonly variablesSchema = model<any>();
  /** Опциональный контекст для VariablesManager (preview рендеров
   *  system-переменных). Сейчас поддержан только agentTitle — для
   *  превью system__agent_title. */
  readonly variableAgentTitle = input<string | null>(null);

  private svc = inject(DynamicVariablesService);
  private elementRef = inject(ElementRef<HTMLInputElement | HTMLTextAreaElement>);
  private renderer = inject(Renderer2);
  private viewContainerRef = inject(ViewContainerRef);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);

  private managerRef?: ComponentRef<VariablesManager>;
  private containerEl?: HTMLElement;
  private scopeSchema?: Signal<any>;

  ngOnInit(): void {
    const scope = this.variableScope();
    const sourceId = this.appVariableTracker();
    this.scopeSchema = this.svc.registerScopeSource(scope, sourceId, this.variablesSchema());

    this.createManagerContainer();

    // Текст source'а → сервису; сервис пересчитает scope-schema.
    effect(() => {
      const t = this.text() ?? '';
      untracked(() => {
        this.svc.setScopeSourceText(scope, sourceId, t);
        this.managerRef?.setInput('text', t);
      });
    }, { injector: this.injector });

    // Новая scope-schema → parent (model) + VariablesManager. JSON-guard
    // против лупы с parent-watcher'ом ниже.
    effect(() => {
      const s = this.scopeSchema!();
      untracked(() => {
        if (JSON.stringify(this.variablesSchema()) !== JSON.stringify(s)) {
          this.variablesSchema.set(s);
        }
        this.managerRef?.setInput('schema', s);
      });
    }, { injector: this.injector });

    // Parent сам сменил schema (reload агента, drawer соседа) → в сервис.
    effect(() => {
      const incoming = this.variablesSchema();
      untracked(() => {
        const current = this.scopeSchema!();
        if (JSON.stringify(incoming) !== JSON.stringify(current)) {
          this.svc.setScopeSchema(scope, incoming);
        }
      });
    }, { injector: this.injector });

    // Смена agentTitle (юзер переименовал агента) → пробрасываем в manager,
    // чтобы preview system__agent_title обновился.
    effect(() => {
      const title = this.variableAgentTitle();
      untracked(() => this.managerRef?.setInput('agentTitle', title));
    }, { injector: this.injector });

    this.destroyRef.onDestroy(() => {
      this.svc.unregisterScopeSource(scope, sourceId);
      this.managerRef?.destroy();
      if (this.containerEl) {
        this.renderer.removeChild(this.elementRef.nativeElement.parentElement, this.containerEl);
      }
    });
  }

  private createManagerContainer(): void {
    this.containerEl = this.renderer.createElement('div');
    this.renderer.addClass(this.containerEl, 'variable-tracker-container');

    const parent = this.elementRef.nativeElement.parentElement;
    const next = this.elementRef.nativeElement.nextSibling;
    if (next) this.renderer.insertBefore(parent, this.containerEl, next);
    else this.renderer.appendChild(parent, this.containerEl);

    this.managerRef = this.viewContainerRef.createComponent(VariablesManager);
    this.managerRef.setInput('textElement', this.elementRef.nativeElement);
    this.managerRef.setInput('text', this.text() ?? '');
    this.managerRef.setInput('schema', this.scopeSchema!());
    this.managerRef.setInput('agentTitle', this.variableAgentTitle());

    // Правки metadata из drawer'а → в сервис.
    this.managerRef.instance.schemaChange.subscribe((s: any) => {
      this.svc.setScopeSchema(this.variableScope(), s);
    });

    this.renderer.appendChild(this.containerEl, this.managerRef.location.nativeElement);
  }
}
